import http from "../../utils/http";
import { getApiBaseUrl } from '@/utils/env'

/**
 * 上传合同文件
 * POST /api/ContractReview/UploadContract
 *
 * @param {File|Blob} file        合同文件（doc/docx/pdf）
 * @param {number} type           文件类型：1=doc/docx，2=pdf
 *                                （FormData + query 双带，与 UploadReferenceFile 的 reviewId 惯例一致）
 * @param {(percent:number)=>void} [onProgress]  上传进度回调
 * @returns {Promise}
 */
export const uploadFile = (file, type, onProgress) => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("type", type);

  // 注意：不要手动设 Content-Type，axios 检测到 FormData 会自动加上
  // 带正确 boundary 的 multipart/form-data 头；手动设反而会让 boundary 丢失，
  // 后端无法解析 multipart body，常导致 500。
  return http.post(`/api/ContractReview/UploadContract?type=${type}`, formData, {
    onUploadProgress: (progressEvent) => {
      if (onProgress && progressEvent.total) {
        const percent = Math.round(
          (progressEvent.loaded / progressEvent.total) * 100,
        );
        onProgress(percent);
      }
    },
  });
};

/**
 * 获取已上传合同的信息（历史查看 ?id=xxx 时使用）
 * GET /api/ContractReview/UploadContract
 *
 * @param {string|number} id  合同 ID（UploadContract 接口返回的 contractId）
 * @returns {Promise}
 */
export const getUploadContract = (id) => {
  return http.get("/api/ContractReview/UploadContract", {
    params: { id },
  });
};

/**
 * 上传参考文件（报价单、招投标文件、补充协议、历史合同版本等）
 * POST /api/ContractReview/UploadReferenceFile
 *
 * @param {File|Blob} file             参考文件
 * @param {string|number} contractId   关联的合同 ID（UploadContract 返回的 contractId）
 * @param {(percent:number)=>void} [onProgress]  上传进度回调
 * @returns {Promise}
 */
export const uploadReferenceFile = (file, contractId, onProgress) => {
  const formData = new FormData();
  formData.append("reviewId", contractId);
  formData.append("file", file);
  return http.post(
    `/api/ContractReview/UploadReferenceFile?reviewId=${contractId}`,
    formData,
    {
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const percent = Math.round(
            (progressEvent.loaded / progressEvent.total) * 100,
          );
          onProgress(percent);
        }
      },
    },
  );
};

/**
 * 获取或开始审查
 * POST /api/ContractReview/GetOrStartReview/{id}
 *
 * 替换原 StartReview：语义上「能取则取，否则才起」——同一合同重复调用会复用既有审查结果，
 * 而不是每次新建。id 走路由参数，其余字段走 body。
 *
 * @param {Object} params
 * @param {string|number} params.id               UploadContract 返回的审查记录 ID（必传，作为路由参数）
 * @param {"PartyA"|"PartyB"} params.stance       审查立场：PartyA=甲方 / PartyB=乙方（必传）
 * @param {string} [params.partyName]             选中的具体主体名称（多甲方/多乙方时区分同 stance 的不同公司，非必传）
 * @param {"Strong"|"Balanced"|"Weak"} params.scale 审查尺度：Strong=强势 / Balanced=均势 / Weak=弱势（必传）
 * @param {string} [params.customRequirement]     自定义审查要求（非必传）
 * @param {string} [params.referenceFileIds]      参考文件 ID 列表，逗号分隔（非必传）
 * @param {boolean} [params.isDraft]              合同状态：true=草稿合同（跳过有效性审查）/ false=正式合同（默认，不传同 false）
 * @returns {Promise}
 */
export const getOrStartReview = (params) => {
  const {
    id,
    stance,
    scale,
    partyName,
    customRequirement,
    referenceFileIds,
    isDraft,
  } = params;
  const payload = { stance, scale };
  if (partyName) payload.partyName = partyName;
  if (customRequirement) payload.customRequirement = customRequirement;
  if (referenceFileIds) payload.referenceFileIds = referenceFileIds;
  if (isDraft !== undefined) payload.isDraft = isDraft;
  return http.post(`/api/ContractReview/GetOrStartReview?id=${id}`, payload);
};

/**
 * 获取已上传文件二进制
 * GET /api/File/GetFile
 *
 * @param {string|number} fileId  UploadContract 接口返回的文件 ID
 * @returns {Promise<Blob>}       文件 Blob，可直接交给 docx 编辑器渲染
 */
export const getFile = (fileId) => {
  return http.get("/api/File/GetFile", {
    params: { fileid: fileId },
    responseType: "blob",
    // 跳过 http 拦截器的"自动下载到本地"逻辑，把 Blob 直接返回给调用方
    skipFileHandler: true,
  });
};

// 根据id 查文件信息  /api/File/GetById?Id=4606101203034091520
export const getGetById = (id) => {
  return http.get(`/api/File/GetById?id=${id}`, {});
};

/**
 * 获取最近审查记录列表
 * GET /api/ContractReview/GetRecentHistory
 *
 * @param {number} [count=10]  返回的记录条数，默认 10
 * @returns {Promise}
 */
export const getRecentHistory = (count = 10) => {
  return http.get("/api/ContractReview/GetRecentHistory", {
    params: { count },
  });
};

/**
 * 分页获取合同审查历史列表
 * POST /api/ContractReview/GetHistoryPage
 *
 * @param {Object} params
 * @param {number} params.page       页码，0-based
 * @param {number} params.pageSize   每页条数
 * @param {string} [params.keyword]  合同标题搜索关键词
 * @returns {Promise} 响应结构兼容 { data: { list: [...], total: number } }
 */
export const getHistoryPage = (params) => {
  return http.post("/api/ContractReview/GetHistoryPage", {
    page: params.page,
    pageSize: params.pageSize,
    keyword: params.keyword,
  });
};

/**
 * 保存审查中编辑过的合同文件
 * POST /api/ContractReview/SaveRevisedFile
 *
 * @param {string|number} id     审查/合同记录 ID（UploadContract 返回的 contractId / GetOrStartReview 的 id 入参），不是 fileId
 * @param {Blob} fileBlob        修改后的 docx 二进制（mime 类型需为 docx）
 * @returns {Promise}
 */
export const saveReviewFile = (id, fileBlob) => {
  const formData = new FormData();
  formData.append("id", id);
  formData.append("file", fileBlob, "document.docx");
  return http.post(`/api/ContractReview/SaveRevisedFile?id=${id}`, formData);
};

// 根据id 查询 审查要求 /api/ContractReview/GetDetailById
export const getGetDetailById = (id) => {
  return http.get(`/api/ContractReview/GetDetailById`, {
    params: { id },
  });
};

// 审查节点查询  api/ContractReview/GetReviewStages
export const getGetReviewStages = (id) => {
  return http.get("/api/ContractReview/GetReviewStages", {
    params: { id },
  });
};

/**
 * 删除合同审查记录
 * POST /api/ContractReview/DeleteHistory
 *
 * @param {string|number} id  审查记录 ID（列表项的 id / contractId）
 * @returns {Promise}
 */
export const deleteReviewHistory = (id) => {
  return http.post(`/api/ContractReview/DeleteHistory?id=${id}`);
};

/**
 * 切换条款修订状态（应用修订时调用）
 * GET /api/ContractReview/ToggleClauseRevision
 *
 * @param {string|number} clauseId  条款 ID
 * @returns {Promise}
 */
export const getToggleClauseRevision = (clauseId) => {
  return http.post(
    `/api/ContractReview/ToggleClauseRevision?clauseId=${clauseId}`,
    {
      params: { clauseId },
    },
  );
};

/**
 * 合同问答（流式响应）
 * POST /api/ContractReview/Ask
 *
 * 后端以 HTTP 流的方式逐段返回答案文本。axios 在浏览器对流的支持不全，
 * 这里直接用 fetch + ReadableStream 读 body，每收到一段文本就回调 onChunk。
 *
 * 鉴权跟 http.js 保持一致：从 localStorage 取 token 加 Bearer 头。
 *
 * 请求体字段（后端约定）：
 *   - contractReviewId  审查记录 ID（即路由 query 上的 id / contractId）
 *   - question          用户输入的问题
 *
 * @param {Object} params
 * @param {string|number} params.id          审查/合同记录 ID（映射到 body.contractReviewId）
 * @param {string} params.question           用户问题
 * @param {(chunk:string)=>void} [params.onChunk]  每收到一段文本回调一次（chunk 是增量）
 * @param {AbortSignal} [params.signal]      用于取消请求（组件卸载 / 用户中断）
 * @returns {Promise<void>}                  流读完（done）后 resolve；非 2xx reject
 */
export const askQuestion = async ({ id, question, onChunk, signal }) => {
  const token = localStorage.getItem("token");
  const baseUrl = getApiBaseUrl();
  const res = await fetch(`${baseUrl}api/ContractReview/Ask`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ contractReviewId: id, question }),
    signal,
  });

  if (!res.ok) {
    const errText = await res.text().catch(() => "");
    throw new Error(`Ask 失败 (${res.status}) ${errText}`);
  }

  // 后端不支持流（content-type 不是流）→ 退化成一次性读
  if (!res.body) {
    const text = await res.text();
    if (onChunk) onChunk(text);
    return;
  }

  /* ============ SSE 解析 ============
   * 后端实际格式（SSE 风格）：
   *   data:{
   *     "content": "您好"
   *   }
   *   \n\n        ← 事件之间用空行分隔
   *   data:{
   *     "content": "！"
   *   }
   *   \n\n
   *   ...
   *
   * 关键点：
   *   1. chunk 边界跟事件边界不对齐 —— 一个 chunk 可能含半个事件、或多个完整事件。
   *      用 buffer 暂存，按 \n\n 切出完整事件后处理，剩下的留到下一个 chunk。
   *   2. 多字节字符（中文）也可能被 chunk 切开。TextDecoder 带 { stream: true }
   *      会在内部缓存不完整的字节序列，下次调用补全 —— 跟 SSE buffer 互不干扰。
   *   3. 兼容 \n\n 和 \r\n\r\n 两种换行风格。
   *
   * @returns {string|null}  content 文本；非 data: 行 / JSON 解析失败返回 null
   */
  const extractContent = (block) => {
    const trimmed = block.trim();
    if (!trimmed.startsWith("data:")) return null;
    const jsonStr = trimmed.slice(5).trim();
    try {
      const parsed = JSON.parse(jsonStr);
      if (parsed && typeof parsed.content === "string") return parsed.content;
    } catch (e) {
      console.warn("[askQuestion] SSE JSON parse failed:", e, jsonStr);
    }
    return null;
  };

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let doneReading = false;
  while (!doneReading) {
    const { done, value } = await reader.read();
    if (done) {
      doneReading = true;
      break;
    }
    buffer += decoder.decode(value, { stream: true });
    // 按"空行"切出完整事件；最后一段可能不完整，留回 buffer 等下一个 chunk
    const events = buffer.split(/\r?\n\r?\n/);
    buffer = events.pop() || "";
    for (const evt of events) {
      const content = extractContent(evt);
      if (content !== null && onChunk) onChunk(content);
    }
  }
  // flush decoder 内可能残留的字节
  const tail = decoder.decode();
  if (tail) buffer += tail;
  // flush 末尾未跟空行的事件
  if (buffer.trim()) {
    const content = extractContent(buffer);
    if (content !== null && onChunk) onChunk(content);
  }
};
